import { getMockContentForApi } from '../data/mockContent';

/**
 * Populate a course from the guide's accumulated state.
 * The course already exists — this adds sections, pages, and modules to it.
 *
 * @param {Object} params
 * @param {number} params.courseId - Existing course ID
 * @param {Object} params.guideState - Full guide state from Redux
 * @param {Function} params.t - i18next translation function
 * @param {Object} params.mutations - RTK Query mutation triggers
 * @param {Function} params.onProgress - Progress callback (0-100)
 * @returns {Promise<{ courseId: number, firstPageId: number|null }>}
 */
export async function populateCourseFromGuide({
  courseId,
  guideState,
  t,
  mutations,
  onProgress = () => {},
}) {
  const {
    tocStructure,
    pageModuleAssignments,
    guideSettings,
  } = guideState;

  const { updateCourse, createSection, createPage, createPageModule, createQuiz } = mutations;

  let firstPageId = null;

  // Count total operations for progress tracking
  let totalOps = 0;
  for (const section of tocStructure) {
    totalOps += 1; // createSection
    for (const page of section.pages) {
      totalOps += 1; // createPage
      const modules = pageModuleAssignments[page.id] || [];
      totalOps += modules.length;
    }
  }
  if (guideSettings.quizEnabled) totalOps += 1; // createQuiz
  totalOps += 1; // final settings update

  let completedOps = 0;
  const reportProgress = () => {
    completedOps++;
    onProgress(Math.round((completedOps / totalOps) * 100));
  };

  // Step B: Create sections and pages sequentially
  for (let sIdx = 0; sIdx < tocStructure.length; sIdx++) {
    const section = tocStructure[sIdx];

    const sectionResult = await createSection({
      courseId,
      name: section.name,
      order: sIdx,
    }).unwrap();

    const realSectionId = sectionResult?.id;
    reportProgress();

    for (let pIdx = 0; pIdx < section.pages.length; pIdx++) {
      const page = section.pages[pIdx];

      const pageResult = await createPage({
        sectionId: realSectionId,
        courseId,
        name: page.name,
        order: pIdx,
      }).unwrap();

      const realPageId = pageResult?.id;
      reportProgress();

      if (!firstPageId && realPageId) {
        firstPageId = realPageId;
      }

      // Create modules for this page
      const modules = pageModuleAssignments[page.id] || [];
      for (const moduleAssignment of modules) {
        const mockContent = getMockContentForApi(moduleAssignment.moduleType, t);

        await createPageModule({
          type: moduleAssignment.moduleType,
          pageId: realPageId,
          newRow: true,
          ...mockContent,
        }).unwrap();
        reportProgress();
      }
    }
  }

  // Step C: Create quiz section if enabled
  if (guideSettings.quizEnabled) {
    await createQuiz({
      courseId,
      name: t('courseGuide.settings.quizSectionName', 'Quiz'),
      descriptionText: '',
      failText: '',
      successText: '',
      welcomeText: '',
      repeatable: true,
      threshold: 80,
      hideAnswers: false,
      quizPages: {},
      settings: {},
    }).unwrap();
    reportProgress();
  }

  // Step D: Update course settings
  const settingsUpdate = {};
  if (guideSettings.diplomaEnabled) {
    settingsUpdate.diploma = {
      passPercentage: guideSettings.diplomaPassPercentage || 80,
    };
    if (guideSettings.diplomaFileId) {
      settingsUpdate.diploma.fileId = guideSettings.diplomaFileId;
    }
  }
  if (guideSettings.gatedAccess || guideSettings.delayedAccess) {
    settingsUpdate.dripContent = {
      gatedAccess: guideSettings.gatedAccess,
      delayedAccess: guideSettings.delayedAccess,
    };
  }
  if (Object.keys(settingsUpdate).length > 0) {
    await updateCourse({
      courseData: { id: courseId, settings: settingsUpdate },
    }).unwrap();
  }
  reportProgress();

  return { courseId, firstPageId };
}
